import * as React from 'react'
import { useOrganization } from '@clerk/clerk-react'
import type {
  ColumnDef,
  RowSelectionState,
  SortingState,
} from '@tanstack/react-table'
import { useQueryState } from 'nuqs'

import { DataTableInfinite } from '@/components/data-table/data-table-infinite'
import { DataTableColumnHeader } from '@/components/data-table/data-table-column-header'
import { useDataTable } from '@/components/data-table/data-table-provider'
import { TabRefreshButton } from '@/components/panel/TabRefreshButton'
import { columnFiltersToFilterConditions } from '@/features/calls/utils/mergeFilters'
import { CLAIMS_PAGE_LIMIT } from '@/features/claims/api/api'
import { useClaimHydrationQuery } from '@/features/claims/hooks/useClaimHydrationQuery'
import { useClaimsQuery } from '@/features/claims/hooks/useClaimsQuery'
import type { ClaimHeader } from '@/features/claims/types'
import { useNuqsAdapter } from '@/lib/store/adapters/nuqs'
import { useFilterActions } from '@/lib/store/hooks/useFilterActions'
import { useFilterState } from '@/lib/store/hooks/useFilterState'
import { DataTableStoreProvider } from '@/lib/store/provider/DataTableStoreProvider'
import { ApiError } from '@/lib/api'
import { ClaimsSheetDetails } from './ClaimsSheetDetails'
import {
  CLAIMS_DEFAULT_SORT,
  CLAIMS_SORTABLE_COLUMNS,
  CLAIMS_TABLE_ID,
  claimsFilterFields,
  claimsSchema,
  claimClientLabel,
  formatClaimCurrency,
  formatClaimDate,
  type ClaimsFilterState,
} from './claimsTableSchema'

const NO_ROW_SELECTION: RowSelectionState = {}
const Unavailable = () => <span className="text-muted-foreground">-</span>

const claimsColumns: ColumnDef<ClaimHeader>[] = [
  {
    accessorKey: 'source_claim_id',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Claim Number" />
    ),
    enableHiding: false,
    cell: ({ row }) => (
      <div className="whitespace-nowrap">{row.original.source_claim_id}</div>
    ),
  },
  {
    id: 'patient_name',
    accessorFn: claimClientLabel,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Client Name" />
    ),
    cell: ({ row }) => (
      <div className="truncate">
        {claimClientLabel(row.original) ?? <Unavailable />}
      </div>
    ),
  },
  {
    accessorKey: 'service_date',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Service Date" />
    ),
    cell: ({ row }) => (
      <div className="whitespace-nowrap">
        {formatClaimDate(row.original.service_date) ?? <Unavailable />}
      </div>
    ),
  },
  {
    accessorKey: 'status',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Status" />
    ),
    cell: ({ row }) => (
      <div className="truncate">{row.original.status || <Unavailable />}</div>
    ),
  },
  ...(['claim_amount', 'paid_amount'] as const).map(
    (accessorKey): ColumnDef<ClaimHeader> => ({
      accessorKey,
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={accessorKey === 'claim_amount' ? 'Billed' : 'Paid'}
        />
      ),
      cell: ({ row }) => (
        <div className="whitespace-nowrap tabular-nums">
          {formatClaimCurrency(row.original[accessorKey]) ?? <Unavailable />}
        </div>
      ),
    })
  ),
  {
    accessorKey: 'provider_name',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Provider Name" />
    ),
    enableSorting: false,
    cell: ({ row }) => (
      <div className="truncate">
        {row.original.provider_name || <Unavailable />}
      </div>
    ),
  },
  {
    accessorKey: 'provider_npi',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Provider NPI" />
    ),
    cell: ({ row }) => (
      <div className="whitespace-nowrap">
        {row.original.provider_npi || <Unavailable />}
      </div>
    ),
  },
]

function claimIsUnavailable(error: unknown): boolean {
  return (
    error instanceof ApiError && (error.status === 403 || error.status === 404)
  )
}

function ClaimsSheet() {
  const { rowSelection, table } = useDataTable<ClaimHeader, unknown>()
  const { setFilter } = useFilterActions()
  const [details, setDetails] = useQueryState('details', {
    history: 'replace',
  })
  const storeState = useFilterState<ClaimsFilterState>()
  const deepLinkId = storeState.id
  const appliedDeepLinkRef = React.useRef(deepLinkId)
  const selectedId = Object.keys(rowSelection)[0] ?? null
  const resolvedSelectedId = selectedId
    ? (table.getRowModel().rows.find((row) => row.id === selectedId)?.id ??
      null)
    : null

  React.useEffect(() => {
    // Do not write an unresolved deep-link ID back after hydration proved that
    // the claim is missing or inaccessible. Only a row present in the table is
    // allowed to become the durable selection URL.
    const deepLinkChanged = deepLinkId !== appliedDeepLinkRef.current
    if (
      resolvedSelectedId &&
      resolvedSelectedId !== deepLinkId &&
      !deepLinkChanged
    ) {
      setFilter('id', resolvedSelectedId)
    }
  }, [deepLinkId, resolvedSelectedId, setFilter])

  const desiredDetails = selectedId && deepLinkId ? '1' : null
  React.useEffect(() => {
    if (details !== desiredDetails) void setDetails(desiredDetails)
  }, [desiredDetails, details, setDetails])

  const clearSelection = React.useCallback(() => {
    setFilter('id', null)
  }, [setFilter])

  React.useEffect(() => {
    const deepLinkChanged = deepLinkId !== appliedDeepLinkRef.current
    appliedDeepLinkRef.current = deepLinkId

    if (!deepLinkId) {
      if (selectedId) table.resetRowSelection()
      return
    }

    const deepLinkIsLoaded = table
      .getRowModel()
      .rows.some((row) => row.id === deepLinkId)
    if (
      deepLinkIsLoaded &&
      selectedId !== deepLinkId &&
      (deepLinkChanged || !selectedId)
    ) {
      table.setRowSelection({ [deepLinkId]: true })
    }
  }, [deepLinkId, selectedId, table])

  return (
    <ClaimsSheetDetails
      onClose={clearSelection}
      onUnavailable={clearSelection}
    />
  )
}

function ClaimsInner() {
  const { organization } = useOrganization()
  const storeState = useFilterState<ClaimsFilterState>()
  const { resetAllFilters, setFilter } = useFilterActions()
  const deepLinkId = storeState.id

  const filters = React.useMemo(() => {
    const filterIds = new Set(
      claimsFilterFields.map((field) => String(field.value))
    )
    return columnFiltersToFilterConditions(
      Object.entries(storeState)
        .filter(([key, value]) => {
          if (!filterIds.has(key) || value === null || value === undefined) {
            return false
          }
          if (Array.isArray(value)) return value.length > 0
          return String(value).trim().length > 0
        })
        .map(([id, value]) => ({ id, value }))
    )
  }, [storeState])

  const sorting = React.useMemo(() => {
    const requested = storeState.sort
    return requested?.id && CLAIMS_SORTABLE_COLUMNS.has(requested.id)
      ? requested
      : CLAIMS_DEFAULT_SORT
  }, [storeState.sort])
  const tableSorting = React.useMemo<SortingState>(() => [sorting], [sorting])

  const query = useClaimsQuery({
    limit: CLAIMS_PAGE_LIMIT,
    sort_by: sorting.id,
    sort_order: sorting.desc ? 'desc' : 'asc',
    filters: filters.length > 0 ? filters : undefined,
  })
  const listRows = React.useMemo(
    () => query.data?.pages.flatMap((page) => page.data) ?? [],
    [query.data]
  )
  const claimIsLoaded = Boolean(
    deepLinkId && listRows.some((claim) => claim.id === deepLinkId)
  )
  const hydration = useClaimHydrationQuery(
    deepLinkId,
    Boolean(deepLinkId && !claimIsLoaded)
  )
  const data = React.useMemo(() => {
    if (!hydration.data || claimIsLoaded) return listRows
    return [...listRows, hydration.data]
  }, [claimIsLoaded, hydration.data, listRows])

  React.useEffect(() => {
    if (!deepLinkId || claimIsLoaded) return
    if (
      (hydration.isSuccess && hydration.data === null) ||
      claimIsUnavailable(hydration.error)
    ) {
      setFilter('id', null)
    }
  }, [
    claimIsLoaded,
    deepLinkId,
    hydration.data,
    hydration.error,
    hydration.isSuccess,
    setFilter,
  ])

  const initialRowSelection = React.useMemo<RowSelectionState>(
    () => (deepLinkId ? { [deepLinkId]: true } : NO_ROW_SELECTION),
    [deepLinkId]
  )
  const getRowId = React.useCallback((claim: ClaimHeader) => claim.id, [])
  const SheetSlot = React.useMemo(() => <ClaimsSheet />, [])
  const ToolbarActions = React.useMemo(
    () => (
      <TabRefreshButton
        onRefresh={query.refetch}
        isFetching={query.isFetching}
      />
    ),
    [query.isFetching, query.refetch]
  )
  const hydratingDeepLink = Boolean(
    deepLinkId && !claimIsLoaded && hydration.isLoading
  )
  const hydrationFailed = Boolean(
    deepLinkId && !claimIsLoaded && hydration.isError
  )
  const refetchClaims = query.refetch
  const refetchHydration = hydration.refetch
  const refetch = React.useCallback(async () => {
    await refetchClaims()
    if (hydrationFailed) await refetchHydration()
  }, [hydrationFailed, refetchClaims, refetchHydration])

  return (
    <DataTableInfinite<ClaimHeader, unknown>
      key={`${sorting.id}:${sorting.desc}`}
      tableId={CLAIMS_TABLE_ID}
      columns={claimsColumns}
      data={data}
      filterFields={claimsFilterFields}
      defaultColumnSorting={tableSorting}
      defaultRowSelection={initialRowSelection}
      manualFiltering
      manualSorting
      isRefetching={query.isPlaceholderData}
      isError={query.isError || hydrationFailed}
      error={query.error ?? (hydrationFailed ? hydration.error : null)}
      entityLabel="claims"
      onClearFilters={resetAllFilters}
      isLoading={query.isLoading || !organization?.id || hydratingDeepLink}
      isFetching={query.isFetching || hydration.isFetching}
      hasNextPage={query.hasNextPage}
      fetchNextPage={query.fetchNextPage}
      refetch={refetch}
      totalRows={query.data?.pages[0]?.pagination.total ?? undefined}
      totalRowsFetched={data.length}
      getRowId={getRowId}
      dataUpdatedAt={query.dataUpdatedAt}
      sheetSlot={SheetSlot}
      toolbarActions={ToolbarActions}
      autoColumnWidth
    />
  )
}

export function ClaimsList() {
  const adapter = useNuqsAdapter(claimsSchema.definition, {
    id: CLAIMS_TABLE_ID,
    history: 'replace',
    namespaceUrlKeys: true,
  })
  return (
    <DataTableStoreProvider adapter={adapter}>
      <ClaimsInner />
    </DataTableStoreProvider>
  )
}
